import i18nFactory from '@yandex-int/i18n';
import classNames from 'classnames';
import {
    TooltipDirection,
    TooltipSize,
    TooltipTheme,
    TooltipView
} from 'lego-on-react';
import * as React from 'react';

import { Bem } from '../../service/Bem/Bem';
import { DocCenterTooltip, HelpLink } from '../../typings/doc-center';
import { DocCenterContent } from '../DocCenterContent/DocCenterContent';
import { StyledLink, StyledLinkTheme } from '../StyledLink/StyledLink';
import { ScopedTooltip } from '../TooltipDecorator/ScopedTooltip';

import './HoverTooltipWrapper.css';
import * as keyset from './HoverTooltipWrapper.i18n';

const i18n = i18nFactory(keyset);

export enum HoverTooltipWrapperTheme {
    clear = 'clear',
    default = 'default'
}

export interface HoverTooltipWrapperProps {
    to: Array<TooltipDirection> | TooltipDirection;
    themeWrapper?: HoverTooltipWrapperTheme;
    theme?: TooltipTheme;
    view?: TooltipView;
    zIndexGroupLevel?: number;
    size?: TooltipSize;
    offset?: number;

    cls?: string;

    id?: DocCenterTooltip;
    onMouseEnter?: (id: DocCenterTooltip) => void;
    tone?: 'default' | 'dark';
    header?: string;
    tooltipText?: string | JSX.Element;
    isDoccenterTooltip?: boolean;
    moreLink?: HelpLink;
    onMoreLinkClick?: (link: HelpLink) => void;
    contentCls?: string;
    tooltipContentCls?: string;
    noTail?: boolean;
    hideOnScroll?: boolean;
}

export interface HoverTooltipWrapperState {
    isTooltipVisible: boolean;
    isElemHovered: boolean;
    hoverWrapperTooltipHovered: boolean;
    isTooltipOnDelay: boolean;
}

export class HoverTooltipWrapper extends React.PureComponent<
    HoverTooltipWrapperProps,
    HoverTooltipWrapperState
> {
    private anchor: React.ReactNode;
    private isComponentMounted: boolean;

    constructor(props: HoverTooltipWrapperProps) {
        super(props);

        this.state = {
            isTooltipVisible: false,
            isElemHovered: false,
            hoverWrapperTooltipHovered: false,
            isTooltipOnDelay: false
        };
    }

    // Фикс для "Can't call setState (or forceUpdate) on an unmounted component."
    // В компоненте используется setTimeout который может вызваться после "Unmount"
    componentWillMount() {
        this.isComponentMounted = true;
    }

    componentWillUnmount() {
        this.isComponentMounted = false;
    }

    render() {
        const {
            children,
            cls,
            tooltipText,
            to,
            theme = 'white',
            view = 'classic',
            size = 'n',
            zIndexGroupLevel,
            offset,
            tone,
            contentCls = '',
            tooltipContentCls,
            themeWrapper = HoverTooltipWrapperTheme.default,
            noTail,
            hideOnScroll
        } = this.props;
        const { isTooltipVisible } = this.state;

        let tooltip = null;
        if (tooltipText !== undefined) {
            tooltip = (
                <ScopedTooltip
                    cls={Bem.cls('hover-tooltip-wrapper', 'tooltip', {
                        theme: themeWrapper
                    })}
                    theme={theme}
                    anchor={this.anchor}
                    visible={isTooltipVisible}
                    tail={!noTail}
                    view={view}
                    to={to}
                    size={size}
                    offset={offset}
                    tone={tone}
                    onMouseOver={this.onHoverWrapperTooltipMouseEnter}
                    onMouseLeave={this.onHoverWrapperTooltipMouseLeave}
                    zIndexGroupLevel={zIndexGroupLevel}
                >
                    <div
                        className={classNames(
                            Bem.cls('hover-tooltip-wrapper', 'content'),
                            tooltipContentCls
                        )}
                    >
                        {this.getHeader()}
                        {this.getContent()}
                        {this.getMoreLink()}
                    </div>
                </ScopedTooltip>
            );
        }

        return (
            <div
                ref={ref => (this.anchor = ref)}
                className={classNames(
                    Bem.cls({
                        block: 'hover-tooltip-wrapper',
                        mods: {
                            theme: themeWrapper
                        }
                    }),
                    cls
                )}
                onWheel={hideOnScroll ? this.onWheel : undefined}
            >
                <div
                    className={contentCls}
                    onMouseEnter={this.onHoverWrapperMouseEnter}
                    onMouseLeave={this.onHoverWrapperMouseLeave}
                >
                    {children}
                </div>
                {tooltip}
            </div>
        );
    }

    private onHoverWrapperMouseEnter = () => {
        const { id, onMouseEnter } = this.props;

        this.setState({
            isTooltipOnDelay: true
        });

        setTimeout(() => {
            if (this.isComponentMounted && this.state.isTooltipOnDelay) {
                this.setState({
                    isTooltipVisible: true,
                    isElemHovered: true,
                    isTooltipOnDelay: false
                });
            }
        }, 300);

        // TODO: проверить что id - из DocCenterTooltip
        if (onMouseEnter) {
            onMouseEnter(id as DocCenterTooltip);
        }
    };

    private onHoverWrapperMouseLeave = () => {
        this.setState({
            isElemHovered: false,
            isTooltipOnDelay: false
        });

        this.hideWithDelay();
    };

    private onWheel = () => {
        const { isTooltipOnDelay, isElemHovered } = this.state;

        if (isTooltipOnDelay || isElemHovered) {
            this.setState({
                isElemHovered: false,
                isTooltipOnDelay: false
            });

            this.hideWithDelay(0);
        }
    };

    private onHoverWrapperTooltipMouseEnter = () => {
        this.setState({
            hoverWrapperTooltipHovered: true
        });
    };

    private onHoverWrapperTooltipMouseLeave = () => {
        this.setState({
            hoverWrapperTooltipHovered: false
        });
        this.hideWithDelay();
    };

    private hideWithDelay = (time: number = 200) => {
        setTimeout(() => {
            if (
                this.state.isElemHovered ||
                this.state.hoverWrapperTooltipHovered
            ) {
                return;
            }
            this.setState({
                isTooltipVisible: false
            });
        }, time);
    };

    private getHeader = (): React.ReactNode => {
        const { header } = this.props;
        if (header === undefined) {
            return null;
        }

        return (
            <h3 className={Bem.cls('hover-tooltip-wrapper', 'header')}>
                {header}
            </h3>
        );
    };

    private getContent = (): React.ReactNode => {
        const { isDoccenterTooltip = true, tooltipText = '' } = this.props;

        if (isDoccenterTooltip) {
            return (
                <DocCenterContent
                    cls={Bem.cls('hover-tooltip-wrapper', 'tooltip-content')}
                    text={tooltipText as string}
                />
            );
        }

        return tooltipText;
    };

    private getMoreLink = (): React.ReactNode => {
        const { moreLink } = this.props;
        if (moreLink === undefined) {
            return null;
        }
        return (
            <StyledLink
                theme={StyledLinkTheme.blue}
                onClick={this.onMoreLinkClick}
                cls={Bem.cls('hover-tooltip-wrapper', 'more')}
            >
                {i18n('about')}
            </StyledLink>
        );
    };

    private onMoreLinkClick = () => {
        const { onMoreLinkClick, moreLink } = this.props;

        this.setState({ isTooltipVisible: false });

        if (moreLink && onMoreLinkClick) {
            onMoreLinkClick(moreLink);
        }
    };
}
